import { XRayAnalyticsType } from '@mm/state/controllers/BotAnalyticsController';
import { selectXRayAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { Empty, Spin } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import styles from './AnalyticsCharts.module.css';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import pluralize from 'pluralize';
import { extractDomainName } from '../../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import { Circle, HelpText } from '@mm/components';

const sortByWDay = (wDay: number[], hits: number[]) => {
  const clone = [...wDay];
  const sortedWDay = clone.sort((a, b) => a - b);
  const remappedHits = sortedWDay.map(value => {
    const index = wDay.findIndex(i => i === value);
    return hits[index];
  });

  return {
    wDay: sortedWDay,
    wDayHits: remappedHits,
  };
};

export const XRayContactsPieChart = ({
  title,
  type,
  footer,
  withLegend,
  busy,
}: {
  title: string;
  type: XRayAnalyticsType;
  footer?: ReactNode;
  withLegend?: boolean;
  busy?: boolean;
}) => {
  const [showLegend, setShowLegend] = useState(false);
  const xRayAnalytics = useSelector(selectXRayAnalytics);
  const analyticsByType = xRayAnalytics?.[type];
  const isWDay = type === 'wday';
  const total = analyticsByType?.total;
  const { wDay, wDayHits } = sortByWDay(
    analyticsByType?.wday || [],
    analyticsByType?.hits || [],
  );
  const labels = isWDay
    ? wDay?.map(dayNum => moment().day(dayNum).format('dddd'))
    : analyticsByType?.emailDomain?.map(
        domain => extractDomainName(domain) || domain,
      );
  const hits = isWDay ? wDayHits : analyticsByType?.hits;

  const backgroundColors = [
    '#6CE5E8',
    '#41B8D5',
    '#2D8BBA',
    '#2F5F98',
    '#31356E',
    '#5E3967',
    '#895273',
    '#B97286',
    '#E28385',
    '#FE9273',
    '#FFA95A',
    '#FFD04C',
    '#C6DE27',
    '#58CB1E',
    '#00AD60',
  ];

  const countPercentage = (value: number) => {
    if (total) {
      const percentage = (value * 100) / total;
      const roundTo = Number.isInteger(percentage) ? 0 : 1;
      return percentage.toFixed(roundTo);
    }
    return value;
  };

  const tooltipCallbacks = {
    label: (tooltipItem: Chart.ChartTooltipItem, data: Chart.ChartData) => {
      const { labels, datasets } = data;
      const { index, datasetIndex } = tooltipItem;

      if (typeof index === 'number' && typeof datasetIndex === 'number') {
        const value = datasets?.[datasetIndex]?.data?.[index] as number;
        const label = labels?.[index];
        return `${label}: ${value} ${pluralize('contact', value)}`;
      }
      return tooltipItem.label || '';
    },
  };

  const data = {
    labels,
    datasets: [
      {
        data: hits,
        backgroundColor: backgroundColors,
        hoverOffset: 4,
      },
    ],
  };

  const options: Chart.ChartOptions = {
    legend: {
      display: false,
    },
    layout: {
      padding: 50,
    },
    tooltips: {
      callbacks: tooltipCallbacks,
    },
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: 'auto',
        color: '#262626',
        align: 'end',
        anchor: 'end',
        textAlign: 'center',
        clamp: true,
        formatter: (value, context) => {
          const label = context?.chart?.data?.labels?.[context.dataIndex];
          return `${label}\n${countPercentage(value)}%`;
        },
        font: {
          size: 12,
          weight: 400,
          family: 'Roboto',
        },
      },
    },
  };

  return (
    <div className={classNames(styles.container, styles.pieChart)}>
      <div className={styles.header}>{title}</div>
      <Spin
        tip="Calculating..."
        size="small"
        spinning={busy}
        wrapperClassName={classNames(styles.spin, {
          [styles.busySpin]: busy || !total,
        })}
      >
        {total ? (
          <>
            <Doughnut
              data={data}
              options={options}
              plugins={[ChartDataLabels]}
              height={400}
              width={500}
            />
            {withLegend && (
              <div className={styles.legendContainer}>
                <div
                  onClick={() => setShowLegend(!showLegend)}
                  className={styles.legendToggle}
                >
                  {showLegend ? 'Hide details' : 'Show details'}
                </div>
                {showLegend && (
                  <div className={styles.legend}>
                    {labels?.map((label, index) => (
                      <div className={styles.legendItem} key={label}>
                        <Circle color={backgroundColors?.[index]} />
                        <HelpText
                          className={styles.legendLabel}
                          text={`${label}: ${hits?.[index]} (${countPercentage(
                            hits?.[index] || 0,
                          )}%)`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {footer}
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            imageStyle={{ height: 'auto' }}
          />
        )}
      </Spin>
    </div>
  );
};
